import {
    type FC,
    useRef
} from "react";
import {
    View
} from "react-native";
import type ICitationSourceCardProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    ExternalLink as ExternalLinkIcon,
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon,
    Globe as GlobeIcon
} from "lucide-react-native";
import {
    getPopoverTabbables,
    focusPopoverElement,
    getPopoverElement
} from "../../../popover/util";
import {
    getSourceDomain,
    getSourceDate
} from "../../util";
import {
    formatNumber
} from "../../../../helpers/locale";
import AnimatedIcon from "../../../animatedIcon";
import Avatar from "../../../avatar";
import Button from "../../../button";
import Text from "../../../text";

const CitationSourceCardOpenIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ExternalLinkIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const CitationSourceCardPreviousIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronLeftIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const CitationSourceCardNextIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronRightIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const CitationSourceCard: FC<ICitationSourceCardProps> = ({
    onSourceIndexChange,
    customLocalize,
    customTheme,
    onOpenPress,
    sourceIndex,
    sources,
    style
}) => {
    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pagerRef = useRef<View>(null);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        container: containerDynamicStyle,
        footer: footerDynamicStyle,
        source: sourceDynamicStyle,
        pager: pagerDynamicStyle,
        head: headDynamicStyle
    } = useStyles({
        spaces
    });

    const currentSource = sources[sourceIndex];

    if(!currentSource) {
        return null;
    }

    const date = getSourceDate({
        locale: activeLocale,
        source: currentSource
    });

    const pagerText = `${formatNumber({
        value: sourceIndex + 1,
        locale: activeLocale
    })} / ${formatNumber({
        value: sources.length,
        locale: activeLocale
    })}`;

    const renderGlobeIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <GlobeIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    const handlePagePress = (nextIndex: number) => {
        const pagerElement = getPopoverElement(pagerRef.current);

        const [
            previousElement,
            nextElement
        ] = pagerElement ? getPopoverTabbables(pagerElement) : [];

        const focusedElement = pagerElement?.ownerDocument.activeElement;

        onSourceIndexChange(nextIndex);

        if(!previousElement || !nextElement || (focusedElement !== previousElement && focusedElement !== nextElement)) {
            return;
        }

        if(nextIndex === 0) {
            focusPopoverElement(nextElement);
        }

        if(nextIndex === sources.length - 1) {
            focusPopoverElement(previousElement);
        }
    };

    const renderPager = () => {
        if(sources.length < 2) {
            return <View />;
        }

        return <View
            style={[
                stylesheet.pager,
                pagerDynamicStyle
            ]}
            ref={pagerRef}
        >
            <Button
                onPress={() => handlePagePress(sourceIndex - 1)}
                accessibilityLabel={localize("previous-source")}
                icon={CitationSourceCardPreviousIcon}
                isDisabled={sourceIndex === 0}
                customTheme={customTheme}
                variant="ghost"
                type="neutral"
                size="small"
            />
            <Text
                customTheme={customTheme}
                variant="labelSmallSize"
                color="mid"
            >
                {pagerText}
            </Text>
            <Button
                onPress={() => handlePagePress(sourceIndex + 1)}
                isDisabled={sourceIndex === sources.length - 1}
                accessibilityLabel={localize("next-source")}
                icon={CitationSourceCardNextIcon}
                customTheme={customTheme}
                variant="ghost"
                type="neutral"
                size="small"
            />
        </View>;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <View
            style={[
                stylesheet.source,
                sourceDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.head,
                    headDynamicStyle
                ]}
            >
                <Avatar
                    customTheme={customTheme}
                    icon={renderGlobeIcon}
                    size="xxSmall"
                    type="neutral"
                />
                <Text
                    style={stylesheet.domain}
                    customTheme={customTheme}
                    variant="labelSmallSize"
                    numberOfLines={1}
                    color="high"
                >
                    {getSourceDomain({
                        source: currentSource
                    })}
                </Text>
                {
                    date ?
                        <Text
                            customTheme={customTheme}
                            variant="labelSmallSize"
                            color="mid"
                        >
                            {`· ${date}`}
                        </Text>
                    :
                        null
                }
            </View>
            <Text
                customTheme={customTheme}
                variant="titleSmallSize"
                numberOfLines={2}
                color="high"
            >
                {currentSource.title}
            </Text>
            {
                currentSource.snippet ?
                    <Text
                        customTheme={customTheme}
                        variant="bodyMediumSize"
                        numberOfLines={3}
                        color="mid"
                    >
                        {currentSource.snippet}
                    </Text>
                :
                    null
            }
        </View>
        <View
            style={[
                stylesheet.footer,
                footerDynamicStyle
            ]}
        >
            {renderPager()}
            <Button
                onPress={() => onOpenPress(currentSource)}
                icon={CitationSourceCardOpenIcon}
                title={localize("open-source")}
                customTheme={customTheme}
                iconDirection="end"
                variant="outline"
                type="neutral"
                size="small"
            />
        </View>
    </View>;
};
export default CitationSourceCard;
